import { 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy, 
  serverTimestamp,
  where
} from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { updateOrderStatus } from '../../orders/services/ordersService';

/**
 * Check if the authenticated user or seller doc has Admin rights.
 * Phone number 1234567890 (or +911234567890) is automatically recognized as Admin.
 */
export const checkIsAdmin = (user, seller) => {
  // Check explicit seller doc properties
  if (seller?.role === 'admin' || seller?.isAdmin === true) {
    return true;
  }

  // Check phone number patterns (ending with 1234567890)
  const phoneCandidates = [
    user?.phoneNumber,
    seller?.userPhone,
    seller?.phone,
    user?.email
  ];

  for (const candidate of phoneCandidates) {
    if (!candidate) continue;
    const cleanDigits = String(candidate).replace(/\D/g, '');
    if (cleanDigits.endsWith('1234567890') || cleanDigits === '1234567890') {
      // Auto-update seller doc in Firestore if not yet marked as admin
      if (seller?.id && (seller.role !== 'admin' || !seller.isAdmin)) {
        updateDoc(doc(db, 'sellers', seller.id), {
          role: 'admin',
          isAdmin: true,
          updatedAt: serverTimestamp()
        }).catch(err => console.warn('Could not update seller admin role in Firestore:', err));
      }
      return true;
    }
  }

  return false;
};

/**
 * Fetch all sellers from 'sellers' collection
 */
export const getAllSellers = async () => {
  try {
    const sellersRef = collection(db, 'sellers');
    const snapshot = await getDocs(sellersRef);
    const sellers = snapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data()
    }));
    
    // Sort by createdAt descending if present
    sellers.sort((a, b) => {
      const timeA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
      const timeB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
      return timeB - timeA;
    });

    return sellers;
  } catch (error) {
    console.error('Error fetching sellers:', error);
    return [];
  }
};

/**
 * Update seller status (e.g. approved, pending, suspended, rejected)
 */
export const updateSellerStatus = async (sellerId, status) => {
  try {
    const sellerRef = doc(db, 'sellers', sellerId);
    await updateDoc(sellerRef, {
      status: status,
      verificationStatus: status === 'approved' ? 'verified' : 'unverified',
      isVerified: status === 'approved',
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating seller status:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update seller profile details by Admin
 */
export const updateSellerDetails = async (sellerId, data) => {
  try {
    const sellerRef = doc(db, 'sellers', sellerId);
    await updateDoc(sellerRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating seller details:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Manually create a new seller account by Admin
 */
export const createSellerByAdmin = async (data) => {
  try {
    const shopNameClean = data.shopName || data.sellerName || "Seller Store";
    const sellerNameClean = data.sellerName || data.shopName || "Seller Store";

    // Generate shopId in exact format: SHOP_LUN_1781116604688_9024
    const prefix = shopNameClean.replace(/[^a-zA-Z0-9]/g, '').substring(0, 3).toUpperCase() || "SHO";
    const timestamp = Date.now();
    const random = Math.floor(1000 + Math.random() * 9000);
    const shopId = data.shopId || `SHOP_${prefix}_${timestamp}_${random}`;
    
    const formattedPhone = data.phoneNumber 
      ? (data.phoneNumber.startsWith('+') ? data.phoneNumber : `+91${data.phoneNumber}`)
      : "";

    // Generate doc ref so userId matches document ID
    const newSellerRef = doc(collection(db, 'sellers'));
    const sellerDocId = newSellerRef.id;

    const sellerData = {
      autoVerified: true,
      businessAddress: data.businessAddress || "",
      businessName: data.businessName || null,
      displayName: shopNameClean,
      email: data.email || "",
      followerCount: 0,
      gstin: data.gstin || null,
      isVerified: true,
      profileImage: data.profileImage || "",
      sellerName: sellerNameClean,
      shopId: shopId,
      shopImage: data.shopImage || "",
      shopName: shopNameClean,
      soldCount: 0,
      status: data.status || "approved",
      submittedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      userId: sellerDocId,
      userPhone: formattedPhone,
      phone: formattedPhone,
      verificationStatus: "verified",
      verifiedAt: serverTimestamp()
    };

    await setDoc(newSellerRef, sellerData);
    
    return { success: true, id: sellerDocId };
  } catch (error) {
    console.error('Error creating seller by admin:', error);
    return { success: false, error: error.message };
  }
};


/**
 * Delete a seller account
 */
export const deleteSellerByAdmin = async (sellerId) => {
  try {
    const sellerRef = doc(db, 'sellers', sellerId);
    await deleteDoc(sellerRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting seller:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Fetch all platform products across all sellers
 */
export const getAllPlatformProducts = async () => {
  try {
    const productsRef = collection(db, 'products');
    const snapshot = await getDocs(productsRef);
    const products = snapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data()
    }));

    products.sort((a, b) => {
      const timeA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
      const timeB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
      return timeB - timeA;
    });

    return products;
  } catch (error) {
    console.error('Error fetching platform products:', error);
    return [];
  }
};

/**
 * Update any product by Admin
 */
export const updateProductByAdmin = async (productId, productData) => {
  try {
    const productRef = doc(db, 'products', productId);
    await updateDoc(productRef, {
      ...productData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating product by admin:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete any product by Admin
 */
export const deleteProductByAdmin = async (productId) => {
  try {
    const productRef = doc(db, 'products', productId);
    await deleteDoc(productRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting product by admin:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create product by Admin
 */
export const createProductByAdmin = async (productData) => {
  try {
    const productId = `PROD_${Date.now()}_${Math.floor(1000 + Math.random() * 9000)}`;
    const productRef = doc(db, 'products', productId);
    
    await setDoc(productRef, {
      ...productData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { success: true, id: productId };
  } catch (error) {
    console.error('Error creating product by admin:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Fetch all platform orders across all sellers and buyers
 */
export const getAllPlatformOrders = async () => {
  try {
    const ordersRef = collection(db, 'orders');
    const snapshot = await getDocs(ordersRef);
    const orders = snapshot.docs.map(docSnap => {
      const data = docSnap.data();
      let createdAt = new Date();
      if (data.createdAt?.toDate) {
        createdAt = data.createdAt.toDate();
      } else if (data.createdAt) {
        createdAt = new Date(data.createdAt);
      }

      const products = Array.isArray(data.products) ? data.products : [];
      const firstItem = products[0] || {};

      let amount = typeof data.amount === 'number' ? data.amount :
                   typeof data.productTotal === 'number' ? data.productTotal :
                   typeof data.total === 'number' ? data.total : 0;

      if (!amount && products.length > 0) {
        amount = products.reduce((sum, p) => sum + (Number(p.price || 0) * Number(p.qty || 1)), 0);
      }

      const rawStatus = data.orderStatus || data.status || 'Pending';
      const statusNormalized = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase();

      return {
        id: docSnap.id,
        ...data,
        createdAt,
        amount: Number(amount) || 0,
        statusNormalized,
        productTitle: firstItem.productTitle || data.productName || 'Order Item',
        productImage: firstItem.productImage || data.productImage || null,
        fullName: data.fullName || data.name || data.address?.name || 'Customer',
        phoneNumber: data.phoneNumber || data.phone || data.address?.phone || '',
        sellerId: data.sellerId || firstItem.sellerId || 'Unknown'
      };
    });

    orders.sort((a, b) => b.createdAt - a.createdAt);
    return orders;
  } catch (error) {
    console.error('Error fetching platform orders:', error);
    return [];
  }
};

/**
 * Update order status by Admin
 */
export const updateOrderByAdmin = async (orderId, newStatus, trackingData = null, buyerId = null) => {
  try {
    return await updateOrderStatus(orderId, newStatus, 'ADMIN', trackingData, buyerId);
  } catch (error) {
    console.error('Error updating order by admin:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Calculate Overview Platform Statistics
 */
export const getPlatformOverviewStats = (sellers = [], products = [], orders = []) => {
  const totalSellers = sellers.length;
  const approvedSellers = sellers.filter(s => s.status === 'approved' || s.status === 'active' || (!s.status && s.isVerified)).length;
  const pendingSellers = sellers.filter(s => s.status === 'pending' || s.status === 'under_review').length;
  const suspendedSellers = sellers.filter(s => s.status === 'suspended' || s.status === 'rejected' || s.status === 'blocked').length;

  const totalProducts = products.length;
  const activeProducts = products.filter(p => p.status === 'active' || p.isActive === true).length;
  const outOfStockProducts = products.filter(p => p.stock === 0 || p.stock === '0' || p.status === 'out_of_stock').length;

  const totalOrders = orders.length;
  let totalRevenue = 0;
  let pendingOrders = 0;
  let deliveredOrders = 0;

  orders.forEach(o => {
    const status = (o.statusNormalized || '').toLowerCase();
    if (status !== 'cancelled' && status !== 'refunded') {
      totalRevenue += (o.amount || 0);
    }
    if (status === 'pending') pendingOrders++;
    if (status === 'delivered') deliveredOrders++;
  });

  return {
    totalSellers,
    approvedSellers,
    pendingSellers,
    suspendedSellers,
    totalProducts,
    activeProducts,
    outOfStockProducts,
    totalOrders,
    pendingOrders,
    deliveredOrders,
    totalRevenue
  };
};

/**
 * Extract Instagram Reel shortcode from URL
 */
export const extractInstagramShortcode = (url) => {
  if (!url) return null;
  const match = String(url).trim().match(/\/(?:p|reel|reels|tv)\/([a-zA-Z0-9_-]+)/);
  if (match && match[1]) return match[1];
  if (/^[a-zA-Z0-9_-]{5,20}$/.test(String(url).trim())) return String(url).trim();
  return null;
};

/**
 * Fetch all platform Instagram Automations from `instagram_mappings`
 */
export const getAllInstagramAutomations = async () => {
  try {
    const mappingsRef = collection(db, 'instagram_mappings');
    const snapshot = await getDocs(mappingsRef);
    const automations = snapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data()
    }));

    // Enriched with seller instagram profile details
    const creatorIds = Array.from(new Set(automations.map(a => a.creatorId).filter(Boolean)));
    const configMap = {};

    await Promise.all(creatorIds.map(async (creatorId) => {
      try {
        const configDoc = await getDoc(doc(db, 'insta_automation', creatorId));
        if (configDoc.exists()) {
          const cfg = configDoc.data()?.config || {};
          configMap[creatorId] = cfg;
        }
      } catch (err) {
        console.warn(`Failed to fetch instagram config for ${creatorId}:`, err);
      }
    }));

    const enrichedAutomations = automations.map(item => {
      const cfg = configMap[item.creatorId] || {};
      return {
        ...item,
        instagramUsername: cfg.instagramUsername || cfg.instagramPageName || item.instagramUsername || null,
        instagramProfilePicture: cfg.instagramProfilePicture || item.instagramProfilePicture || null,
        instagramConnected: cfg.instagramConnected !== undefined ? cfg.instagramConnected : true,
        instagramAccountId: cfg.instagramAccountId || item.instagramAccountId || null
      };
    });

    enrichedAutomations.sort((a, b) => {
      const timeA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
      const timeB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
      return timeB - timeA;
    });

    return enrichedAutomations;
  } catch (error) {
    console.error('Error fetching all instagram automations:', error);
    return [];
  }
};

/**
 * Create or update Instagram Reel Automation by Admin
 */
export const createInstaAutomationByAdmin = async (data) => {
  try {
    const {
      sellerId,
      instagramUrl,
      taggedProducts = [],
      keyword = 'price, link, buy',
      replyMessage = 'Check your DMs! The product link is waiting for you there! 📥✨',
      dmMessage = 'Here is your exclusive product link: {link} 🛍️'
    } = data;

    if (!sellerId) return { success: false, error: 'Seller store selection is required' };
    if (!instagramUrl) return { success: false, error: 'Instagram Reel link is required' };

    const shortcode = extractInstagramShortcode(instagramUrl);
    if (!shortcode) return { success: false, error: 'Invalid Instagram Reel URL format. Example: https://www.instagram.com/reel/C8xY2k2O4x9/' };

    const postId = `video_admin_${Date.now()}_${Math.floor(1000 + Math.random() * 9000)}`;

    const taggedProductIds = taggedProducts.map(p => p.id || p.productId).filter(Boolean);
    const primaryProduct = taggedProducts[0] || {};

    const mappingPayload = {
      creatorId: sellerId,
      instagramUrl: instagramUrl.trim(),
      instagramShortcode: shortcode,
      reelkartPostId: postId,
      keyword: keyword.trim() || 'price, link, buy',
      replyMessage: replyMessage.trim() || 'Check your DMs! The product link is waiting for you there! 📥✨',
      dmMessage: dmMessage.trim() || 'Here is your exclusive product link: {link} 🛍️',
      taggedProductIds,
      taggedProducts: taggedProducts.map(p => ({
        productId: p.id || p.productId,
        name: p.title || p.name || 'Product',
        price: Number(p.price) || 0,
        imageUrl: p.imageUrl || p.image || p.thumbnailUrl || '',
        shopName: p.shopName || ''
      })),
      createdBy: 'admin',
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp()
    };

    // 1. Set mapping document in `instagram_mappings` keyed by shortcode
    const mappingRef = doc(db, 'instagram_mappings', shortcode);
    await setDoc(mappingRef, mappingPayload, { merge: true });

    // 2. Create post document in `posts` collection
    const postRef = doc(db, 'posts', postId);
    await setDoc(postRef, {
      postId,
      userId: sellerId,
      creatorId: sellerId,
      instagramUrl: instagramUrl.trim(),
      instagramMediaId: shortcode,
      instagramShortcode: shortcode,
      hasInstagramMapping: true,
      instagramKeyword: keyword.trim(),
      instagramReplyMessage: replyMessage.trim(),
      title: primaryProduct.title || primaryProduct.name || 'Instagram Reel Product',
      videoUrl: primaryProduct.imageUrl || primaryProduct.image || '',
      thumbnailUrl: primaryProduct.imageUrl || primaryProduct.image || '',
      taggedProducts: mappingPayload.taggedProducts,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });

    // 3. Ensure creator `insta_automation` config has automation enabled
    const creatorAutomationRef = doc(db, 'insta_automation', sellerId);
    const creatorAutomationSnap = await getDoc(creatorAutomationRef);
    if (!creatorAutomationSnap.exists()) {
      await setDoc(creatorAutomationRef, {
        config: {
          instagramAutomationEnabled: true,
          instagramConnected: false,
          subscriptionStatus: 'active',
          updatedAt: serverTimestamp()
        }
      }, { merge: true });
    } else {
      await updateDoc(creatorAutomationRef, {
        'config.instagramAutomationEnabled': true,
        'config.updatedAt': serverTimestamp()
      }).catch(() => {});
    }

    return { success: true, shortcode, postId };
  } catch (error) {
    console.error('Error creating Instagram automation by admin:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete Instagram Reel Automation by Admin
 */
export const deleteInstaAutomationByAdmin = async (mappingId) => {
  try {
    const mappingRef = doc(db, 'instagram_mappings', mappingId);
    await deleteDoc(mappingRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting instagram automation:', error);
    return { success: false, error: error.message };
  }
};

